import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class PointType extends BaseObj {
    public ID: number;
    public __id: number;

    public Calculated: boolean;
    public __calculated: boolean;

    public IncludeInRaceContestantPoints: boolean;
    public __includeinracecontestantpoints: boolean;

    public Name: string;
    public __name: string;

    public UseForRanking: boolean;
    public __useforranking: boolean;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Calculated?: boolean;
        EntityState?: EntityState;
        IncludeInRaceContestantPoints?: boolean;
        Name?: string | null;
        UseForRanking?: boolean;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<PointType>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('PointType tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Calculated = values.Calculated ?? false;
        this.__calculated = values.Calculated ?? false;
        Object.defineProperty(this, 'Calculated', {
            get(): boolean {
                return this.__calculated;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Calculated', this.__calculated, value);
                this.__calculated = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IncludeInRaceContestantPoints = values.IncludeInRaceContestantPoints ?? false;
        this.__includeinracecontestantpoints = values.IncludeInRaceContestantPoints ?? false;
        Object.defineProperty(this, 'IncludeInRaceContestantPoints', {
            get(): boolean {
                return this.__includeinracecontestantpoints;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IncludeInRaceContestantPoints', this.__includeinracecontestantpoints, value);
                this.__includeinracecontestantpoints = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.UseForRanking = values.UseForRanking ?? false;
        this.__useforranking = values.UseForRanking ?? false;
        Object.defineProperty(this, 'UseForRanking', {
            get(): boolean {
                return this.__useforranking;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('UseForRanking', this.__useforranking, value);
                this.__useforranking = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Calculated": ${JSON.stringify(this.Calculated)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"IncludeInRaceContestantPoints": ${JSON.stringify(this.IncludeInRaceContestantPoints)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"UseForRanking": ${JSON.stringify(this.UseForRanking)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of PointType from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Calculated?: boolean;
        IncludeInRaceContestantPoints?: boolean;
        Name?: string | null;
        UseForRanking?: boolean;
        EntityState?: 'Added' | 'Unchanged';
    }): PointType {
        return new PointType(values);
    }

    public static _FromServerJson(json: any): PointType {
        return new PointType({
            ID: json.ID,
            Calculated: json.Calculated,
            IncludeInRaceContestantPoints: json.IncludeInRaceContestantPoints,
            Name: json.Name ?? '',
            UseForRanking: json.UseForRanking,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of PointType from another instance of PointType.
     * @param other The PointType you want to clone
     * @returns A cloned PointType
     */
    public static FromOther(other: PointType): PointType {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return PointType._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return PointType.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'Calculated' | 'IncludeInRaceContestantPoints' | 'Name' | 'UseForRanking')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'PointType';
}
